import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Avatar from "../../component/Avatar";
import sponsorApi from "../../apis/organiserApis/sponsor.api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faSearch,
  faTimes,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

const BRAND = "#1d275f";

const reasonMap = {
  pending_invite: "Sponsor has a pending invitation.",
  previously_rejected_same_pack:
    "Sponsor previously rejected this package. Choose another package.",
  null: null,
};

function Badge({ children, tone = "default", className = "" }) {
  const toneMap = {
    default: "bg-gray-100 text-gray-700",
    info: "bg-blue-50 text-blue-700",
    success: "bg-green-50 text-green-700",
    warn: "bg-amber-50 text-amber-700",
    danger: "bg-red-50 text-red-700",
    brand: "bg-[var(--brand-bg)] text-[var(--brand-fg)]",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        toneMap[tone] || toneMap.default
      } ${className}`}
      style={{ "--brand-bg": "#eef0fb", "--brand-fg": BRAND }}
    >
      {children}
    </span>
  );
}

const formatEUR = (v) => {
  try {
    const n = Number(v ?? 0);
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `€${v}`;
  }
};

export default function SendCampaignInvites() {
  const navigate = useNavigate();
  const { campaignId } = useParams();

  const [data, setData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [campaignError, setCampaignError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [sponsors, setSponsors] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [selectedPackId, setSelectedPackId] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState({ remaining_daily_quota: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!campaignId) {
      setData(null);
      setPackages([]);
      setLoadingCampaign(false);
      return;
    }
    setLoadingCampaign(true);
    sponsorApi.getCampaignById({
      campaignId,
      success: ({ data }) => {
        setData(data);
        setPackages(Array.isArray(data?.packages) ? data.packages : []);
        setCampaignError(null);
        setLoadingCampaign(false);
      },
      error: (err) => {
        console.error("Failed to fetch campaign data:", err);
        setErrorSafe(
          "Unable to load campaign details. Please try again later."
        );
        setLoadingCampaign(false);
      },
    });

    function setErrorSafe(msg) {
      setCampaignError(msg);
    }
  }, [campaignId]);

  const fetchSponsors = (opts = {}) => {
    const { reset = false } = opts;
    if (reset) {
      setPage(1);
      setSponsors([]);
      setHasMore(false);
    }
    setLoading(reset);
    setLoadingMore(!reset && page > 1);

    sponsorApi.listAvailableSponsors({
      campaignId,
      params: {
        selected_pack_id: selectedPackId || undefined,
        search: search || undefined,
        page: reset ? 1 : page,
        limit,
      },
      success: (res) => {
        const dataPayload = res?.data?.data || res?.sponsors || [];
        const returnedMeta = (res?.meta || res?.data?.meta) ?? {};
        if (reset) {
          setSponsors(Array.isArray(dataPayload) ? dataPayload : []);
        } else {
          setSponsors((prev) => [
            ...prev,
            ...(Array.isArray(dataPayload) ? dataPayload : []),
          ]);
        }
        setMeta(returnedMeta);
        setHasMore(
          Boolean(returnedMeta?.has_more ?? dataPayload?.length >= limit)
        );
      },
      error: (err) => {
        console.error("listAvailableSponsors error:", err);
        toast.error(err?.response?.data?.message || "Failed to load sponsors.");
      },
      final: () => {
        setLoading(false);
        setLoadingMore(false);
      },
    });
  };

  useEffect(() => {
    if (!loadingCampaign) {
      fetchSponsors({ reset: true });
    }
  }, [loadingCampaign, selectedPackId]);

  useEffect(() => {
    const t = setTimeout(() => fetchSponsors({ reset: true }), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    sponsorApi.listAvailableSponsors({
      campaignId,
      selected_pack_id: selectedPackId || undefined,
      search: search || undefined,
      page: nextPage,
      limit,
      success: (res) => {
        const dataPayload = res?.data || res?.data?.data || [];
        setSponsors((prev) => [
          ...prev,
          ...(Array.isArray(dataPayload) ? dataPayload : []),
        ]);
        const returnedMeta = (res?.meta || res?.data?.meta) ?? {};
        setMeta(returnedMeta);
        setHasMore(
          Boolean(returnedMeta?.has_more ?? dataPayload?.length >= limit)
        );
      },
      error: (err) => {
        console.error("listAvailableSponsors loadMore error:", err);
        toast.error(
          err?.response?.data?.message || "Failed to load more sponsors."
        );
      },
      final: () => {
        setLoadingMore(false);
      },
    });
  };

  const isSponsorSelected = (sId) => selected.some((x) => x.sponsor_id === sId);

  const toggleSelect = (s) => {
    if (!s.eligible) {
      toast.info(
        reasonMap[s.reason] || "This sponsor cannot be invited right now."
      );
      return;
    }

    const quota = meta.remaining_daily_quota;
    if (
      quota != null &&
      selected.length >= quota &&
      !isSponsorSelected(s.sponsor_id)
    ) {
      toast.warn(`You may send up to ${quota} more invitations today.`);
      return;
    }

    if (
      quota == null &&
      selected.length >= 5 &&
      !isSponsorSelected(s.sponsor_id)
    ) {
      toast.warn(
        "Daily invite cap is 5; you have reached the client-side limit."
      );
      return;
    }

    if (isSponsorSelected(s.sponsor_id)) {
      setSelected((prev) => prev.filter((x) => x.sponsor_id !== s.sponsor_id));
    } else {
      setSelected((prev) => [...prev, s]);
    }
  };

  const removeSelected = (sponsorId) => {
    setSelected((prev) => prev.filter((x) => x.sponsor_id !== sponsorId));
  };

  const handleSubmit = () => {
    if (!selected.length) {
      toast.error("Select at least one sponsor to invite.");
      return;
    }

    const campType = data?.campaign?.type;
    if (
      campType === "monetary" &&
      (!selectedPackId || selectedPackId === null)
    ) {
      toast.error("Please select a package for monetary campaigns.");
      return;
    }

    const quota = meta.remaining_daily_quota;
    if (quota != null && selected.length > quota) {
      toast.error(
        `Selected ${selected.length} but only ${quota} invites allowed today.`
      );
      return;
    }

    if (message.length > 1000) {
      toast.error("Message must be 1000 characters or fewer.");
      return;
    }

    setSubmitting(true);
    const payload = {
      sponsorIds: selected.map((s) => s.sponsor_id),
      message: message.trim() || null,
      selected_pack_id: selectedPackId || undefined,
    };

    sponsorApi.sendCampaignInvites({
      campaignId,
      payload,
      success: (res) => {
        const requestIds = res?.data?.requestIds || res?.requestIds || null;
        if (Array.isArray(requestIds)) {
          toast.success(`Invites sent to ${requestIds.length} sponsor(s).`);
        } else {
          toast.success("Invites processed.");
        }
        setSelected([]);
        setMessage("");
        fetchSponsors({ reset: true });
      },
      error: (err) => {
        const serverMsg = err?.response?.data?.message || err?.message;
        const details =
          err?.response?.data?.details || err?.response?.data?.errors;
        if (Array.isArray(details)) {
          details.forEach((d) => {
            toast.error(
              `Sponsor ${d.sponsorId || d.sponsor_id}: ${
                d.error || d.reason || JSON.stringify(d)
              }`
            );
          });
        } else {
          toast.error(serverMsg || "Failed to send invites.");
        }
      },
      final: () => {
        setSubmitting(false);
      },
    });
  };

  const PacksPanel = () => {
    const campType = data?.campaign?.type;
    if (loadingCampaign) {
      return <div className="text-sm text-gray-400">Loading packages…</div>;
    }
    if (!packages || packages.length === 0) {
      return (
        <div className="text-sm text-gray-400">
          No packages available for this campaign.
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="text-sm font-medium text-gray-700">Packages</div>
        <div className="grid grid-cols-1 sm:grid-cols-1 gap-2">
          {packages.map((pack) => {
            const isSelected =
              Number(pack.package_id) === Number(selectedPackId);
            return (
              <button
                key={pack.package_id}
                type="button"
                onClick={() => setSelectedPackId(pack.package_id)}
                className={`w-full text-left px-3 py-3 rounded-xl border transition-shadow ${
                  isSelected
                    ? "shadow-sm border-[color:var(--brand)] bg-[rgba(29,39,95,0.04)]"
                    : "hover:bg-gray-50"
                }`}
                style={{ "--brand": BRAND }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {pack.pack_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      ID: {pack.package_id}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">
                      {formatEUR(pack.organizer_net)}
                    </div>
                    <div className="text-xs text-gray-400">Organizer net</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {campType === "monetary" ? (
          <div className="text-xs text-rose-600 mt-1">
            Package selection is required for monetary campaigns.
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Invite Sponsors
          </h2>
          <p className="text-sm text-gray-500 max-w-xl">
            Invite verified sponsors to this campaign. Ineligible sponsors are
            disabled with contextual guidance.
          </p>
        </div>

        <div className="text-sm text-right">
          <div className="text-xs text-gray-500">Remaining quota</div>
          <div className="font-semibold text-lg" style={{ color: BRAND }}>
            {meta.remaining_daily_quota == null
              ? "—"
              : `${meta.remaining_daily_quota}`}
          </div>
          <div className="text-xs text-gray-400">per day</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left: Sponsor list */}
        <div className="md:col-span-2">
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-3">
            <div className="flex gap-3 items-center mb-3">
              <div className="relative flex-1">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search sponsors by name, company or id…"
                  className="w-full rounded-xl border-gray-200 px-4 py-2 focus:ring-0"
                />
                <div className="absolute right-3 top-2.5">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                </div>
              </div>
              <div className="hidden sm:block">
                <button
                  onClick={() => {
                    const picks = sponsors
                      .filter((s) => s.eligible)
                      .slice(0, 5);
                    setSelected((prev) => {
                      const merged = [...prev];
                      picks.forEach((p) => {
                        if (!merged.some((m) => m.sponsor_id === p.sponsor_id))
                          merged.push(p);
                      });
                      return merged;
                    });
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white text-sm"
                >
                  Quick pick
                </button>
              </div>
            </div>

            {/* List */}
            <div className="divide-y">
              {loading && sponsors.length === 0 ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse py-4 flex items-center gap-3"
                  >
                    <div className="h-12 w-12 rounded-full bg-gray-200" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-1/3" />
                    </div>
                    <div className="h-8 w-24 rounded bg-gray-200" />
                  </div>
                ))
              ) : sponsors.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  No sponsors match your query.
                </div>
              ) : (
                sponsors.map((s) => {
                  const ineligible = !s.eligible;
                  const reasonText = reasonMap[s.reason] || s.reason;
                  const selectedFlag = isSponsorSelected(s.sponsor_id);
                  return (
                    <div
                      key={s.sponsor_id}
                      className={`py-3 px-2 flex items-center gap-3 ${
                        ineligible ? "opacity-70" : "hover:bg-gray-50"
                      } rounded-lg`}
                    >
                      <button
                        onClick={() => toggleSelect(s)}
                        disabled={ineligible}
                        className="flex items-center gap-3 flex-1 text-left"
                        type="button"
                        aria-pressed={selectedFlag}
                      >
                        <Avatar
                          name={s.name}
                          imageUrl={s.logo_url || undefined}
                          size={48}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-sm truncate">
                              {s.name}
                            </div>
                            {s.is_verified ? (
                              <Badge tone="success" className="text-xs">
                                Verified
                              </Badge>
                            ) : null}
                            {s.company_name ? (
                              <div className="text-xs text-gray-400 truncate">
                                {s.company_name}
                              </div>
                            ) : null}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            ID: {s.sponsor_id}
                          </div>
                        </div>
                      </button>

                      <div className="flex flex-col items-end gap-2">
                        {ineligible ? (
                          <div className="flex items-center gap-2 text-xs text-amber-700">
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              className="text-sm"
                            />
                            <span title={reasonText || "Not eligible"}>
                              {reasonText ? reasonText : "Not eligible"}
                            </span>
                          </div>
                        ) : (
                          <div
                            className={`px-3 py-1 rounded-lg text-xs font-medium ${
                              selectedFlag
                                ? "bg-[rgba(29,39,95,0.08)] text-[color:var(--brand)]"
                                : "bg-gray-100 text-gray-700"
                            }`}
                            style={{ "--brand": BRAND }}
                          >
                            {selectedFlag ? "Selected" : "Invite"}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer load more */}
            <div className="mt-3 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Showing {sponsors.length} sponsor(s)
              </div>
              <div>
                {hasMore ? (
                  <button
                    onClick={loadMore}
                    className="px-3 py-2 text-sm rounded-xl border bg-white"
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Packages (top), Selected + message + send */}
        <div className="md:col-span-1">
          <div className="sticky top-6 space-y-4">
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-4">
              <div className="mb-2">
                <div className="text-sm font-semibold">Campaign</div>
                <div className="text-xs text-gray-500">
                  {data?.campaign?.title ?? `#${campaignId}`}
                </div>
              </div>

              {/* Packages */}
              <div className="mb-3">
                <PacksPanel />
              </div>

              <div className="mb-2">
                <div className="text-sm font-semibold">Selected</div>
                <div className="text-xs text-gray-400">
                  {selected.length} chosen
                </div>
              </div>

              <div className="space-y-2 max-h-36 overflow-auto pb-2">
                {selected.length === 0 ? (
                  <div className="text-xs text-gray-400">
                    No sponsors selected
                  </div>
                ) : (
                  selected.map((s) => (
                    <div key={s.sponsor_id} className="flex items-center gap-3">
                      <Avatar
                        name={s.name}
                        imageUrl={s.logo_url || undefined}
                        size={36}
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {s.name}
                        </div>
                        <div className="text-xs text-gray-400">
                          ID: {s.sponsor_id}
                        </div>
                      </div>
                      <button
                        onClick={() => removeSelected(s.sponsor_id)}
                        className="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-full border hover:bg-gray-50"
                        aria-label="Remove"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-3">
                <label className="text-xs font-medium text-gray-700">
                  Message (optional)
                </label>
                <textarea
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Optional message to sponsors (max 1000 chars)"
                  maxLength={1000}
                  className="mt-2 w-full rounded-xl border-gray-200 px-3 py-2 text-sm"
                />
                <div className="mt-1 text-xs text-gray-400 text-right">
                  {message.length}/1000
                </div>
              </div>
            </div>

            {/* Action area */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-4">
              <div className="text-xs text-gray-500 mb-3">
                Tip: You can invite multiple sponsors at once. Server enforces a
                daily cap.
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !selected.length}
                  className={`w-full px-4 py-2 rounded-xl text-white inline-flex items-center justify-center gap-2 ${
                    submitting ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  style={{ background: BRAND }}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  {submitting
                    ? "Sending…"
                    : `Send ${selected.length ? `to ${selected.length}` : ""}`}
                </button>

                <button
                  onClick={() => {
                    setSelected([]);
                    setMessage("");
                  }}
                  className="w-full px-4 py-2 rounded-xl border bg-white text-sm"
                >
                  Clear
                </button>

                <button
                  onClick={() => navigate(-1)}
                  className="w-full px-4 py-2 rounded-xl border bg-white text-sm"
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky bar */}
      <div className="md:hidden fixed left-0 right-0 bottom-0 p-3 bg-white border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="flex-1 text-sm">
            <div className="font-semibold">{selected.length} selected</div>
            <div className="text-xs text-gray-400">
              {meta.remaining_daily_quota == null
                ? "Daily cap: 5"
                : `Remaining: ${meta.remaining_daily_quota}`}
            </div>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting || !selected.length}
            className={`px-4 py-2 rounded-xl text-white inline-flex items-center gap-2 ${
              submitting ? "opacity-60 cursor-not-allowed" : ""
            }`}
            style={{ background: BRAND }}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            {submitting ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
